<template>
  <div class="chart-wrapper">
    <h3 class="chart-title">{{ $t(title) }}</h3>
    <vue-c3 class="chart-box" :handler="handler" />
  </div>
</template>

<script>
import Vue from 'vue';
import VueC3 from 'vue-c3';
import 'c3/c3.css';

export default {
  name: 'TrendChart',
  components: {
    VueC3
  },
  props: {
    data: {
      type: Array,
      required: true,
      default: () => []
    },
    title: {
      type: String,
      default: 'health.aggregateData.hoursOfSleep'
    },
    yLabel: {
      type: String,
      default: 'Horas'
    },
    color: {
      type: String,
      default: '#e06666'
    },
    cumulative: {
      type: Boolean,
      default: true
    },
    formatScale: {
      type: Boolean,
      default: false
    },
    chartType: {
      type: String,
      default: 'area'
    }
  },
  data () {
    return {
      handler: new Vue()
    };
  },
  watch: {
    data: {
      handler () {
        this.draw();
      },
      deep: true
    }
  },
  mounted () {
    this.draw();
    window.addEventListener('resize', this.redrawOnResize);
  },
  methods: {
    draw () {
      let values = [];

      if (this.cumulative) {
        this.data.forEach((val, i) => {
          if (i === 0) values.push(val);
          else values.push(parseFloat((values[i - 1] + val).toFixed(1)));
        });
      } else {
        values = [...this.data];
      }

      const days = values.map((_, i) => i + 1);
      days.unshift(0);
      values.unshift(0);

      const options = {
        data: {
          x: 'x',
          columns: [
            ['x', ...days],
            ['value', ...values]
          ],
          type: this.chartType,
          labels: false,
          colors: {
            value: this.color
          },
          groups: [['value']]
        },
        area: {
          show: true
        },
        legend: {
          show: false
        },
        wiggle: {
          enabled: true
        },
        axis: {
          x: {
            label: {
              text: 'Día del mes',
              position: 'outer-middle'
            },
            tick: {
              values: days,
              format: d => d === 0 ? '' : d.toString()
            },
            min: 0,
            padding: {
              left: -0.5,
              right: 0.5
            }
          },
          y: {
            min: 0,
            padding: { top: 60, bottom: 0 },
            label: {
              text: this.yLabel,
              position: 'outer-middle'
            },
            tick: this.formatScale
              ? {
                format: d => {
                  const formatNum = (num) => {
                    const fixed = (num).toFixed(1).replace('.', ',');
                    return fixed.endsWith(',0') ? fixed.slice(0, -2) : fixed;
                  };

                  if (d >= 1000000) {
                    return `${formatNum(d / 1000000)} millones`;
                  } else if (d >= 1000) {
                    return `${formatNum(d / 1000)} mil`;
                  } else {
                    return new Intl.NumberFormat('es-ES').format(d);
                  }
                }
              }
              : {}
          }
        },
        point: {
          show: true
        },
        tooltip: {
          grouped: false,
          format: {
            value: v => `${v} ${this.yLabel}`
          }
        },
        color: {
          pattern: [this.color]
        }
      };

      this.handler.$emit('init', options);
    }
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.redrawOnResize);
  }
};
</script>

<style scoped>
.chart-wrapper {
  flex: 1 1 100%;
  margin: 12px;
  padding: 20px;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 0 0 1px rgba(224, 102, 102, 0.15),
  0 10px 20px rgba(0, 0, 0, 0.04);
  overflow: visible;
}

.chart-title {
  font-size: 1.5rem;
  font-weight: 500;
  margin-bottom: 20px;
  text-align: center;
  color: #1e1e1e;
  font-family: 'Outfit', 'Segoe UI', sans-serif;
}

.chart-box {
  width: 100% !important;
  height: 280px !important;
  min-height: 250px;
}

</style>

<style scoped>

.c3-circle {
  r: 3px !important; /* size of the dot */
  fill: #fff !important; /* inner color = hollow */
  stroke: #e06666 !important; /* border color (your line color) */
  stroke-width: 2px;
  transition: transform 0.2s ease;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.1));
}

.c3-circle:hover {
  transform: scale(1.5);
  filter: drop-shadow(0 0 4px rgba(0, 0, 0, 0.3));
  cursor: pointer;
}

.c3-line {
  stroke-width: 3px;
  filter: drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.1));
}

.c3-area {
  fill: #e57914 !important;
  opacity: 0.25 !important;
}
</style>



// WEBPACK FOOTER //
// src/brand/health/components/summary/DashboardChart.vue